import { useState, useEffect, useRef, memo } from 'react'
import { useGitHubToken } from '../hooks/useGitHubToken'
import { useI18n } from '../i18n/I18nContext'
import { graphqlRequest } from '../lib/graphql'

interface ReadmePreviewProps {
  owner: string
  repo: string
  onClose: () => void
}

const README_QUERY = `
query Readme($owner: String!, $name: String!) {
  readme(owner: $owner, name: $name) {
    readmeHtml
    owner
    name
  }
}
`

type ReadmeResult = {
  readme: { readmeHtml: string; owner: string; name: string } | null
}

export const ReadmePreview = memo(function ReadmePreview({
  owner,
  repo,
  onClose
}: ReadmePreviewProps) {
  const { t } = useI18n()
  const tRef = useRef(t)
  tRef.current = t
  const { token: githubToken } = useGitHubToken()
  const [readme, setReadme] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchReadme = async () => {
      const tr = tRef.current
      try {
        setLoading(true)
        setError(null)

        const headers: HeadersInit = {}
        if (githubToken) {
          headers['X-GitHub-Token'] = githubToken
        }

        const data = await graphqlRequest<ReadmeResult>(
          README_QUERY,
          { owner, name: repo },
          headers
        )

        if (!data.readme) {
          setError(tr('readme.notFound'))
          return
        }

        setReadme(data.readme.readmeHtml || '')
      } catch (err) {
        setError(err instanceof Error ? err.message : tr('readme.loadError'))
      } finally {
        setLoading(false)
      }
    }

    fetchReadme()
  }, [owner, repo, githubToken])

  return (
    <div className="readme-modal-overlay" onClick={onClose}>
      <div className="readme-modal" onClick={(e) => e.stopPropagation()}>
        <div className="readme-modal-header">
          <div>
            <h3>{t('readme.title', { repo })}</h3>
            <p className="readme-modal-subtitle">{t('readme.subtitle', { owner, repo })}</p>
          </div>
          <button type="button" className="readme-modal-close" onClick={onClose} aria-label={t('readme.closeAria')}>
            ✕
          </button>
        </div>

        <div className="readme-modal-content">
          {loading && (
            <div className="readme-loading">
              <div className="spinner spinner-medium"></div>
              <p>{t('readme.loading')}</p>
            </div>
          )}

          {error && (
            <div className="readme-error">
              <span className="readme-error-icon">⚠️</span>
              <p>{error}</p>
            </div>
          )}

          {readme && !loading && !error && (
            <div
              className="readme-content"
              dangerouslySetInnerHTML={{ __html: readme }}
            />
          )}

          {!readme && !loading && !error && (
            <div className="readme-empty">
              <span className="readme-empty-icon">📄</span>
              <p>{t('readme.empty')}</p>
            </div>
          )}
        </div>

        <div className="readme-modal-footer">
          <a
            href={`https://github.com/${owner}/${repo}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
          >
            {t('readme.openGithub')}
          </a>
          <button type="button" onClick={onClose} className="btn-primary">
            {t('readme.close')}
          </button>
        </div>
      </div>
    </div>
  )
})
